import { useEffect } from 'react';

import { FormikProvider } from 'formik';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { useDynamicForm } from './hooks/use-dynamic-form';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { useNuveiSubmit } from '../../../../hooks/use-nuvei-submit';
import { SelectField } from '../../../../../SelectField/SelectField';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';

interface NuveiAPMProps {
  onReady: () => void;
}

export function NuveiAPM({ onReady }: NuveiAPMProps) {
  const { selectedPaymentMethod } = useCheckout();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { formik, formFields } = useDynamicForm();
  const { handleAPMPay } = useNuveiSubmit();
  const { useTaxCalculationField, showAddressField } = useShowTaxCalculationFields();
  const { AddressElementRef } = useAddressElementRef();
  const { featureFlags } = useAppConfig();
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;

  const selectedPaymentMethodId = selectedPaymentMethod?.id;

  useEffect(() => {
    const timeout = setTimeout(onReady, 300);
    return () => clearTimeout(timeout);
  }, [onReady, selectedPaymentMethodId]);

  const onPayClicked = () => {
    formik.submitForm();
    AddressElementRef.current?.validate();
    handleAPMPay(formik.values, formik.isValid, AddressElementRef.current);
  };

  return (
    <PaymentElementContainer
      content={
        <FormikProvider value={formik}>
          <Stack spacing={1.5}>
            {formFields.map((field) => {
              if (field.type === 'text') {
                return (
                  <InputField
                    key={field.name}
                    name={field.name}
                    label={shouldHideFieldsLabels ? '' : t(field.displayName)}
                    inputProps={{
                      placeholder: t(field.displayName),
                    }}
                  />
                );
              }
              if (field.type === 'list') {
                return (
                  <SelectField
                    key={field.name}
                    name={field.name}
                    label={shouldHideFieldsLabels ? '' : t(field.displayName)}
                    options={[]}
                    selectProps={{
                      placeholder: t(field.displayName),
                    }}
                  />
                );
              }
              return <></>;
            })}
            {useTaxCalculationField && (
              <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
            )}
          </Stack>
        </FormikProvider>
      }
    >
      <PayButton onClick={onPayClicked} />
    </PaymentElementContainer>
  );
}
